"use client";

import {
  check_auth,
  createBlog,
  getBlogCategories,
  getBlogs,
  getStudentDetail,
} from "@/api";
import CommentEditor from "@/app/components/CommentBox/CommentEditor";
import { isAdminUser } from "@/app/lib/isAdminUser";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

const initialForm = {
  title: "",
  short_description: "",
  category: [],
  sub_category: [],
  author: "",
  cover_image: null,
  content: "",
};

const normalizeCategoryValue = (value) => String(value || "").trim();
const parseArrayLikeString = (value) => {
  if (typeof value !== "string") return null;
  let current = value.trim();
  for (let i = 0; i < 2; i += 1) {
    if (!(current.startsWith("[") && current.endsWith("]"))) break;
    try {
      const parsed = JSON.parse(current);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === "string") {
        current = parsed.trim();
        continue;
      }
    } catch {
      break;
    }
  }
  return null;
};
const toStringArray = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeCategoryValue(item)).filter(Boolean);
  }
  const parsed = parseArrayLikeString(value);
  if (parsed) {
    return parsed.map((item) => normalizeCategoryValue(item)).filter(Boolean);
  }
  const normalized = normalizeCategoryValue(value);
  return normalized ? [normalized] : [];
};
const dedupeByLowerCase = (values = []) => {
  const map = new Map();
  values.forEach((value) => {
    const normalized = normalizeCategoryValue(value);
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (!map.has(key)) map.set(key, normalized);
  });
  return Array.from(map.values());
};
const normalizeBlogCategories = (payload) => {
  const list = Array.isArray(payload)
    ? payload
    : payload?.categories ||
      payload?.items ||
      payload?.data ||
      payload?.result ||
      payload?.category ||
      [];

  if (!Array.isArray(list)) return [];

  const deduped = new Map();
  list.forEach((item) => {
    const label =
      typeof item === "string"
        ? normalizeCategoryValue(item)
        : normalizeCategoryValue(
            item?.category ||
              item?.name ||
              item?.title ||
              item?.value ||
              item?.label ||
              item?.slug,
          );
    if (!label) return;
    const key = label.toLowerCase();
    if (!deduped.has(key)) deduped.set(key, label);
  });

  return Array.from(deduped.values());
};
const normalizeBlogTaxonomy = (payload) => {
  const categories = normalizeBlogCategories(payload);
  const rawSubCategories =
    payload?.sub_categories ||
    payload?.subCategories ||
    payload?.sub_category ||
    payload?.data?.sub_categories ||
    payload?.data?.subCategories ||
    [];
  const subCategories = dedupeByLowerCase(toStringArray(rawSubCategories));
  return { categories, subCategories };
};
const normalizeCollection = (payload, keys) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  if (Array.isArray(payload.data)) return payload.data;
  if (payload.data && typeof payload.data === "object") {
    for (const key of keys) {
      if (Array.isArray(payload.data[key])) return payload.data[key];
    }
  }
  if (Array.isArray(payload.result)) return payload.result;
  return [];
};

export default function PublishBlogPage() {
  const router = useRouter();
  const [formData, setFormData] = useState(initialForm);
  const [blogCategories, setBlogCategories] = useState([]);
  const [blogSubCategories, setBlogSubCategories] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [categoryDraft, setCategoryDraft] = useState("");
  const [subCategoryDraft, setSubCategoryDraft] = useState("");
  const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
  const [isAddingNewSubCategory, setIsAddingNewSubCategory] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const session = await check_auth();
        if (!session) {
          router.replace("/login");
          return;
        }

        const user = await getStudentDetail();
        const hasAccess = isAdminUser(user);
        setHasAdminAccess(hasAccess);
        if (!hasAccess) return;

        try {
          const [categoryPayload, blogPayload] = await Promise.all([
            getBlogCategories(),
            getBlogs(),
          ]);
          const taxonomy = normalizeBlogTaxonomy(categoryPayload);
          const normalizedBlogs = normalizeCollection(blogPayload, [
            "blogs",
            "blog",
            "items",
          ]);
          setBlogCategories(taxonomy.categories);
          setBlogSubCategories(taxonomy.subCategories);
          setBlogs(normalizedBlogs);
        } catch (categoryError) {
          setBlogCategories([]);
          setBlogSubCategories([]);
          setBlogs([]);
        }
      } catch (error) {
        setHasAdminAccess(false);
      } finally {
        setIsCheckingAccess(false);
      }
    };

    checkAccess();
  }, [router]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const addUnique = (values = [], value) =>
    dedupeByLowerCase([...values, value]);
  const removeValue = (values = [], valueToRemove) => {
    const target = normalizeCategoryValue(valueToRemove).toLowerCase();
    return values.filter(
      (value) => normalizeCategoryValue(value).toLowerCase() !== target,
    );
  };
  const onAddCategory = (value) => {
    const normalized = normalizeCategoryValue(value);
    if (!normalized) return;
    setFormData((prev) => ({
      ...prev,
      category: addUnique(prev.category, normalized),
    }));
    setCategoryDraft("");
    setIsAddingNewCategory(false);
  };
  const onRemoveCategory = (value) => {
    setFormData((prev) => ({
      ...prev,
      category: removeValue(prev.category, value),
    }));
  };
  const onAddSubCategory = (value) => {
    const normalized = normalizeCategoryValue(value);
    if (!normalized) return;
    setFormData((prev) => ({
      ...prev,
      sub_category: addUnique(prev.sub_category, normalized),
    }));
    setSubCategoryDraft("");
    setIsAddingNewSubCategory(false);
  };
  const onRemoveSubCategory = (value) => {
    setFormData((prev) => ({
      ...prev,
      sub_category: removeValue(prev.sub_category, value),
    }));
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, cover_image: file }));
  };
  const availableSubCategories = useMemo(() => {
    if (!Array.isArray(formData.category) || formData.category.length === 0) {
      return blogSubCategories;
    }
    const deduped = new Map();
    blogs.forEach((blog) => {
      const blogCategoriesNormalized = toStringArray(blog?.category).map(
        (item) => item.toLowerCase(),
      );
      const intersects = formData.category.some((category) =>
        blogCategoriesNormalized.includes(
          normalizeCategoryValue(category).toLowerCase(),
        ),
      );
      if (!intersects) return;
      toStringArray(blog?.sub_category || blog?.subCategory).forEach(
        (subCategory) => {
          const key = subCategory.toLowerCase();
          if (!deduped.has(key)) deduped.set(key, subCategory);
        },
      );
    });
    return dedupeByLowerCase([
      ...blogSubCategories,
      ...Array.from(deduped.values()),
    ]);
  }, [blogSubCategories, blogs, formData.category]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!hasAdminAccess) return;

    setStatus({ type: "", message: "" });
    setIsSubmitting(true);

    if (!formData.cover_image) {
      setStatus({ type: "error", message: "Cover image file is required." });
      setIsSubmitting(false);
      return;
    }

    const categories = dedupeByLowerCase(formData.category);
    if (!categories.length) {
      setStatus({
        type: "error",
        message: "At least one category is required.",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      await createBlog({
        ...formData,
        category: categories,
        sub_category: dedupeByLowerCase(formData.sub_category),
      });
      setStatus({ type: "success", message: "Blog published successfully." });
      setFormData(initialForm);
      setCategoryDraft("");
      setSubCategoryDraft("");
      setIsAddingNewCategory(false);
      setIsAddingNewSubCategory(false);
      try {
        const [categoryPayload, blogPayload] = await Promise.all([
          getBlogCategories(),
          getBlogs(),
        ]);
        const taxonomy = normalizeBlogTaxonomy(categoryPayload);
        const normalizedBlogs = normalizeCollection(blogPayload, [
          "blogs",
          "blog",
          "items",
        ]);
        setBlogCategories(taxonomy.categories);
        setBlogSubCategories(taxonomy.subCategories);
        setBlogs(normalizedBlogs);
      } catch (categoryError) {
        // Keep current form behavior even if category refresh fails.
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Could not publish blog. Check auth/permissions and required fields.";
      setStatus({ type: "error", message });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isCheckingAccess) {
    return (
      <section className="w-full py-[72px] px-[16px] lg:px-[80px] min-h-screen">
        <div className="max-w-4xl mx-auto">
          <p className="text-gray-600">Checking access...</p>
        </div>
      </section>
    );
  }

  if (!hasAdminAccess) {
    return (
      <section className="w-full py-[72px] px-[16px] lg:px-[80px] min-h-screen">
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm p-6 lg:p-10 border border-gray-100">
          <h1 className="text-2xl font-bold text-[#1A1A1A] mb-3">
            Access denied
          </h1>
          <p className="text-gray-600 mb-6">
            Only admin users can access blog publishing.
          </p>
          <Link
            href="/blog"
            className="text-primary font-medium hover:underline"
          >
            Back to Blogs
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full py-[72px] lg:py-[96px] px-[16px] lg:px-[80px] bg-[#f7fbff] min-h-screen">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm p-6 lg:p-10 border border-gray-100">
        <div className="flex items-center justify-between mb-8 gap-4">
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1A1A1A]">
            Publish Blog
          </h1>
          <Link
            href="/blog"
            className="text-sm font-medium text-primary hover:underline"
          >
            Back to Blogs
          </Link>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-5">
          <input
            name="title"
            value={formData.title}
            onChange={onChange}
            placeholder="Blog title"
            className="w-full border border-gray-300 rounded-md px-4 py-3 outline-none focus:border-[#0D6FEA]"
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex flex-col gap-2">
              {!isAddingNewCategory && blogCategories.length > 0 ? (
                <select
                  value=""
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "__new__") {
                      setIsAddingNewCategory(true);
                      return;
                    }
                    onAddCategory(value);
                  }}
                  className="w-full border border-gray-300 rounded-md px-4 py-3 outline-none focus:border-[#0D6FEA] bg-white"
                >
                  <option value="">Add category from list</option>
                  {blogCategories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                  <option value="__new__">+ Add new category</option>
                </select>
              ) : null}
              {isAddingNewCategory || blogCategories.length === 0 ? (
                <div className="flex gap-2">
                  <input
                    value={categoryDraft}
                    onChange={(e) => setCategoryDraft(e.target.value)}
                    placeholder="Add new category"
                    className="w-full border border-gray-300 rounded-md px-4 py-3 outline-none focus:border-[#0D6FEA]"
                  />
                  <button
                    type="button"
                    onClick={() => onAddCategory(categoryDraft)}
                    className="px-3 rounded-md border border-gray-300 text-gray-700 cursor-pointer"
                  >
                    Add
                  </button>
                  {blogCategories.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => {
                        setIsAddingNewCategory(false);
                        setCategoryDraft("");
                      }}
                      className="px-3 rounded-md border border-gray-300 text-gray-700 cursor-pointer"
                    >
                      Cancel
                    </button>
                  ) : null}
                </div>
              ) : null}
              <div className="flex flex-wrap gap-2">
                {formData.category.map((category) => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => onRemoveCategory(category)}
                    className="text-xs px-3 py-1 rounded-full border border-gray-300 bg-gray-50"
                  >
                    {category} ×
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {!isAddingNewSubCategory && availableSubCategories.length > 0 ? (
                <select
                  value=""
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "__new__") {
                      setIsAddingNewSubCategory(true);
                      return;
                    }
                    onAddSubCategory(value);
                  }}
                  className="w-full border border-gray-300 rounded-md px-4 py-3 outline-none focus:border-[#0D6FEA] bg-white"
                >
                  <option value="">Add sub-category from list</option>
                  {availableSubCategories.map((subCategory) => (
                    <option key={subCategory} value={subCategory}>
                      {subCategory}
                    </option>
                  ))}
                  <option value="__new__">+ Add new sub-category</option>
                </select>
              ) : null}
              {isAddingNewSubCategory || availableSubCategories.length === 0 ? (
                <div className="flex gap-2">
                  <input
                    value={subCategoryDraft}
                    onChange={(e) => setSubCategoryDraft(e.target.value)}
                    placeholder="Add new sub-category (optional)"
                    className="w-full border border-gray-300 rounded-md px-4 py-3 outline-none focus:border-[#0D6FEA]"
                  />
                  <button
                    type="button"
                    onClick={() => onAddSubCategory(subCategoryDraft)}
                    className="px-3 rounded-md border border-gray-300 text-gray-700 cursor-pointer"
                  >
                    Add
                  </button>
                  {availableSubCategories.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => {
                        setIsAddingNewSubCategory(false);
                        setSubCategoryDraft("");
                      }}
                      className="px-3 rounded-md border border-gray-300 text-gray-700 cursor-pointer"
                    >
                      Cancel
                    </button>
                  ) : null}
                </div>
              ) : null}
              <div className="flex flex-wrap gap-2">
                {formData.sub_category.map((subCategory) => (
                  <button
                    key={subCategory}
                    type="button"
                    onClick={() => onRemoveSubCategory(subCategory)}
                    className="text-xs px-3 py-1 rounded-full border border-gray-300 bg-gray-50"
                  >
                    {subCategory} ×
                  </button>
                ))}
              </div>
            </div>
            <input
              name="author"
              value={formData.author}
              onChange={onChange}
              placeholder="Author"
              className="w-full border border-gray-300 rounded-md px-4 py-3 outline-none focus:border-[#0D6FEA]"
              required
            />
          </div>
          <input
            name="cover_image"
            type="file"
            accept="image/*"
            onChange={onFileChange}
            className="w-full border border-gray-300 rounded-md px-4 py-3 outline-none focus:border-[#0D6FEA]"
            required
          />

          <textarea
            name="short_description"
            value={formData.short_description}
            onChange={onChange}
            placeholder="Short description"
            rows={3}
            className="w-full border border-gray-300 rounded-md px-4 py-3 outline-none focus:border-[#0D6FEA]"
          />

          <div>
            <p className="text-sm text-gray-700 mb-2">Blog content</p>
            <CommentEditor
              showSubmitButton={false}
              withTopSpacing={false}
              showAlignmentControls={true}
              onChange={(content) =>
                setFormData((prev) => ({ ...prev, content }))
              }
            />
          </div>

          {status.message && (
            <p
              className={`text-sm ${
                status.type === "success" ? "text-green-700" : "text-red-600"
              }`}
            >
              {status.message}
            </p>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !formData.content.trim()}
            className="w-full md:w-fit px-6 py-3 bg-primary text-white rounded-md font-medium disabled:opacity-60 cursor-pointer"
          >
            {isSubmitting ? "Publishing..." : "Publish Blog"}
          </button>
        </form>
      </div>
    </section>
  );
}
